// ─────────────────────────────────────────────────────────────────────────────
// Visual editor for imported full-page document designs.
//
// WHY THE DOM IS THE SOURCE OF TRUTH
// The design arrives as generated HTML — dozens of absolutely-positioned
// elements the converter produced from a PDF. Mirroring that into React state
// would mean parsing markup into a model, re-serialising on every keystroke,
// and losing anything the model failed to represent. Instead the markup is
// mounted once and edited in place, exactly as a browser already knows how to
// do: selection is an attribute, a drag writes `style.left`, typing goes
// through contentEditable. Saving serialises the container back to a string.
//
// The cost is that React does not re-render on canvas edits, so anything the
// panels display is mirrored into `revision` to force a read. That is a
// deliberate trade: one explicit nudge, versus a parse/serialise cycle per
// keypress across ~60 elements.
//
// UNITS
// Points throughout, matching the converter and the stored template. The canvas
// is scaled for display only; every pointer delta is divided back by that scale
// before it is written, so a design edited at 60% zoom stores true page
// coordinates.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Bold, Italic, Image as ImageIcon, Eye, Save, Trash2, Type,
  AlignLeft, AlignCenter, AlignRight, Wand2, ZoomIn, ZoomOut, Plus,
} from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';

export interface TemplateAsset {
  assetId: string;
  kind: 'logo' | 'signature' | 'stamp' | 'image';
  url: string;
  page: number;
  left: number;
  top: number;
  width: number;
  height: number;
  opacity: number;
}

export interface TokenGroup {
  group: string;
  tokens: Array<{ token: string; label: string; sample: string; inLoop?: boolean; block?: boolean }>;
}

export interface TokenSuggestion {
  id: string;
  token: string;
  matched: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

interface Props {
  templateId: string;
  bodyHtml: string;
  bodyCss: string;
  assets: TemplateAsset[];
  suggestions?: TokenSuggestion[];
  onSaved: () => void;
}

const FONT_STACKS = [
  { label: 'Arial / Helvetica', value: "Arial, Helvetica, sans-serif" },
  { label: 'Times New Roman', value: "'Times New Roman', Times, serif" },
  { label: 'Courier New', value: "'Courier New', Courier, monospace" },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
];

/** Reads a numeric pt value off an inline style, tolerating an absent unit. */
function readPt(el: HTMLElement, prop: 'left' | 'top' | 'width' | 'height'): number {
  const raw = el.style[prop];
  if (!raw) return 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function readFontSize(el: HTMLElement): number {
  const n = parseFloat(el.style.fontSize || '');
  return Number.isFinite(n) ? n : 10;
}

/** Normalises any CSS colour the browser reports back to #rrggbb for <input type=color>. */
function toHexColour(value: string): string {
  if (!value) return '#000000';
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();
  const m = value.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (!m) return '#000000';
  return (
    '#' +
    [m[1], m[2], m[3]]
      .map((c) => Number(c).toString(16).padStart(2, '0'))
      .join('')
  );
}

export function TemplateDesignEditor({
  templateId, bodyHtml, bodyCss, assets, suggestions = [], onSaved,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [zoom, setZoom] = useState(0.75);
  const [dirty, setDirty] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [tokenGroups, setTokenGroups] = useState<TokenGroup[]>([]);
  const [pendingSuggestions, setPendingSuggestions] = useState<TokenSuggestion[]>(suggestions);
  const [localAssets, setLocalAssets] = useState<TemplateAsset[]>(assets);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setPendingSuggestions(suggestions), [suggestions]);
  useEffect(() => setLocalAssets(assets), [assets]);

  // Mount the design once per template. Re-running on every bodyHtml change
  // would wipe uncommitted canvas edits the moment a save round-trips.
  useEffect(() => {
    if (canvasRef.current) canvasRef.current.innerHTML = bodyHtml;
    setSelectedId(null);
    setDirty(false);
    setRevision((r) => r + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => {
    api
      .get<{ groups: TokenGroup[] }>('/api/documents/templates/token-catalog')
      .then((r) => setTokenGroups(r.data.groups))
      .catch(() => setTokenGroups([]));
  }, []);

  const selectedEl = useMemo(() => {
    void revision;
    if (!selectedId || !canvasRef.current) return null;
    return canvasRef.current.querySelector<HTMLElement>(`[data-zx-id="${selectedId}"]`);
  }, [selectedId, revision]);

  const touch = useCallback(() => {
    setDirty(true);
    setRevision((r) => r + 1);
  }, []);

  // ── Selection ──────────────────────────────────────────────────────────────

  const handleCanvasClick = (event: React.MouseEvent) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-zx-id]');
    if (!target) {
      setSelectedId(null);
      return;
    }
    setSelectedId(target.getAttribute('data-zx-id'));
  };

  // Outline the selection without touching the markup that gets saved: a
  // stylesheet keyed on an attribute keeps the indicator out of the saved HTML,
  // where an injected wrapper or inline outline would end up printed.
  useEffect(() => {
    const root = canvasRef.current;
    if (!root) return;
    root.querySelectorAll('[data-zx-sel]').forEach((el) => el.removeAttribute('data-zx-sel'));
    if (selectedId) {
      root.querySelector(`[data-zx-id="${selectedId}"]`)?.setAttribute('data-zx-sel', '1');
    }
  }, [selectedId, revision]);

  // ── Dragging ───────────────────────────────────────────────────────────────

  const handleMouseDown = (event: React.MouseEvent) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-zx-id]');
    if (!target || target.isContentEditable) return;

    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const originLeft = readPt(target, 'left');
    const originTop = readPt(target, 'top');
    let moved = false;

    const onMove = (e: MouseEvent) => {
      // Pointer deltas are in screen pixels; the canvas is scaled for display,
      // so dividing by zoom is what keeps a drag 1:1 with the cursor and the
      // stored coordinate in true page points.
      const dx = (e.clientX - startX) / zoom;
      const dy = (e.clientY - startY) / zoom;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 1.5) return;
      moved = true;
      target.style.left = `${Math.round((originLeft + dx) * 100) / 100}pt`;
      target.style.top = `${Math.round((originTop + dy) * 100) / 100}pt`;
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (moved) touch();
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // ── Property edits ─────────────────────────────────────────────────────────

  const applyStyle = (prop: string, value: string) => {
    if (!selectedEl) return;
    selectedEl.style.setProperty(prop, value);
    touch();
  };

  const applyText = (value: string) => {
    if (!selectedEl) return;
    selectedEl.textContent = value;
    touch();
  };

  const toggleBold = () => {
    if (!selectedEl) return;
    const isBold = (selectedEl.style.fontWeight || '') === '700';
    selectedEl.style.fontWeight = isBold ? '400' : '700';
    touch();
  };

  const toggleItalic = () => {
    if (!selectedEl) return;
    const isItalic = (selectedEl.style.fontStyle || '') === 'italic';
    selectedEl.style.fontStyle = isItalic ? 'normal' : 'italic';
    touch();
  };

  const deleteSelected = () => {
    if (!selectedEl) return;
    selectedEl.remove();
    setSelectedId(null);
    touch();
  };

  const addTextBox = () => {
    const page = canvasRef.current?.querySelector<HTMLElement>('.zx-page');
    if (!page) return;
    const id = `new-${Date.now().toString(36)}`;
    const el = document.createElement('div');
    el.className = 'zx-text';
    el.setAttribute('data-zx-id', id);
    el.setAttribute('data-zx-kind', 'text');
    el.style.cssText = 'left:40pt;top:40pt;font-size:10pt;font-family:Arial, Helvetica, sans-serif;color:#000000';
    el.textContent = 'New text';
    page.appendChild(el);
    setSelectedId(id);
    touch();
  };

  const insertToken = (token: string) => {
    if (!selectedEl) {
      toast.error('Select an element first, then insert the field.');
      return;
    }
    selectedEl.textContent = `${selectedEl.textContent ?? ''}{{${token}}}`;
    touch();
  };

  // ── Assets ─────────────────────────────────────────────────────────────────

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'logo');
      form.append('page', '1');
      form.append('left', '40');
      form.append('top', '30');
      form.append('width', '120');
      const { data } = await api.post<{ asset: TemplateAsset }>(
        `/api/documents/templates/${templateId}/assets`,
        form,
      );
      return data.asset;
    },
    onSuccess: (asset) => {
      setLocalAssets((prev) => [...prev, asset]);
      toast.success('Logo added. Drag it into place, then save.');
      onSaved();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not upload this image.')),
  });

  const updateAsset = useMutation({
    mutationFn: async ({ assetId, patch }: { assetId: string; patch: Partial<TemplateAsset> }) =>
      (await api.patch(`/api/documents/templates/${templateId}/assets/${assetId}`, patch)).data,
    onSuccess: () => onSaved(),
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not update this image.')),
  });

  const removeAsset = useMutation({
    mutationFn: async (assetId: string) =>
      (await api.delete(`/api/documents/templates/${templateId}/assets/${assetId}`)).data,
    onSuccess: (_d, assetId) => {
      setLocalAssets((prev) => prev.filter((a) => a.assetId !== assetId));
      toast.success('Image removed.');
      onSaved();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not remove this image.')),
  });

  // ── Tokens, preview, save ──────────────────────────────────────────────────

  const acceptSuggestion = useMutation({
    mutationFn: async (accepted: TokenSuggestion[]) => {
      const html = canvasRef.current?.innerHTML ?? '';
      const { data } = await api.post<{ html: string; applied: number; skipped: Array<{ reason: string }> }>(
        '/api/documents/templates/apply-tokens',
        { html, accepted },
      );
      return data;
    },
    onSuccess: (data, accepted) => {
      if (canvasRef.current) canvasRef.current.innerHTML = data.html;
      setPendingSuggestions((prev) =>
        prev.filter((s) => !accepted.some((a) => a.id === s.id && a.token === s.token)),
      );
      setSelectedId(null);
      touch();
      if (data.skipped.length) toast(data.skipped[0].reason);
      else toast.success(`${data.applied} field${data.applied === 1 ? '' : 's'} linked.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not link these fields.')),
  });

  const preview = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ html: string }>('/api/documents/templates/preview', {
        bodyHtml: canvasRef.current?.innerHTML ?? '',
        bodyCss,
        assets: localAssets,
      });
      return data.html;
    },
    onSuccess: (html) => setPreviewHtml(html),
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not render the preview.')),
  });

  const save = useMutation({
    mutationFn: async () => {
      const root = canvasRef.current;
      if (!root) throw new Error('Nothing to save.');
      // Strip the editor-only selection marker so it never reaches storage.
      root.querySelectorAll('[data-zx-sel]').forEach((el) => el.removeAttribute('data-zx-sel'));
      root.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
      return (await api.patch(`/api/documents/templates/${templateId}`, { bodyHtml: root.innerHTML })).data;
    },
    onSuccess: () => {
      setDirty(false);
      toast.success('Design saved.');
      onSaved();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the design.')),
  });

  // Guard against losing canvas edits to a stray navigation or refresh.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const isText = selectedEl?.getAttribute('data-zx-kind') === 'text';

  return (
    <div className="space-y-4">
      <style>{`
        .zx-editor-canvas [data-zx-id]{cursor:move}
        .zx-editor-canvas [data-zx-id]:hover{outline:1px dashed #94a3b8}
        .zx-editor-canvas [data-zx-sel]{outline:2px solid #2563eb !important;outline-offset:1px}
        ${bodyCss || ''}
      `}</style>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
          <Save className="mr-1 h-4 w-4" /> {dirty ? 'Save design' : 'Saved'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => preview.mutate()} disabled={preview.isPending}>
          <Eye className="mr-1 h-4 w-4" /> Preview with sample data
        </Button>
        <Button size="sm" variant="secondary" onClick={addTextBox}>
          <Plus className="mr-1 h-4 w-4" /> Add text
        </Button>
        <Button size="sm" variant="secondary" onClick={() => logoInputRef.current?.click()} disabled={uploadLogo.isPending}>
          <ImageIcon className="mr-1 h-4 w-4" /> Add logo
        </Button>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadLogo.mutate(file);
            e.target.value = '';
          }}
        />
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.max(0.25, z - 0.15))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="w-12 text-center text-data-sm">{Math.round(zoom * 100)}%</span>
          <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.min(2, z + 0.15))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_300px]">
        {/* Canvas */}
        <Card>
          <CardBody className="overflow-auto bg-slate-100 p-4" style={{ maxHeight: '70vh' }}>
            <div
              className="zx-editor-canvas origin-top"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
              ref={canvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
            />
          </CardBody>
        </Card>

        {/* Panels */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Selected element" />
            <CardBody className="space-y-3">
              {!selectedEl ? (
                <p className="text-body-sm text-on-surface-variant">
                  Click any element on the page to edit it.
                </p>
              ) : (
                <>
                  {isText && (
                    <label className="block">
                      <span className="mb-1 block text-body-sm text-on-surface-variant">Text</span>
                      <textarea
                        className="w-full rounded border border-outline bg-surface p-2 text-data-md"
                        rows={3}
                        value={selectedEl.textContent ?? ''}
                        onChange={(e) => applyText(e.target.value)}
                      />
                    </label>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="mb-1 block text-body-sm text-on-surface-variant">X (pt)</span>
                      <Input
                        type="number"
                        value={readPt(selectedEl, 'left')}
                        onChange={(e) => applyStyle('left', `${e.target.value}pt`)}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-body-sm text-on-surface-variant">Y (pt)</span>
                      <Input
                        type="number"
                        value={readPt(selectedEl, 'top')}
                        onChange={(e) => applyStyle('top', `${e.target.value}pt`)}
                      />
                    </label>
                  </div>

                  {isText && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                          <span className="mb-1 block text-body-sm text-on-surface-variant">Size (pt)</span>
                          <Input
                            type="number"
                            step="0.5"
                            value={readFontSize(selectedEl)}
                            onChange={(e) => applyStyle('font-size', `${e.target.value}pt`)}
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-body-sm text-on-surface-variant">Colour</span>
                          <input
                            type="color"
                            className="h-9 w-full rounded border border-outline bg-surface"
                            value={toHexColour(selectedEl.style.color)}
                            onChange={(e) => applyStyle('color', e.target.value)}
                          />
                        </label>
                      </div>

                      <label className="block">
                        <span className="mb-1 block text-body-sm text-on-surface-variant">Font</span>
                        <select
                          className="h-9 w-full rounded border border-outline bg-surface px-2 text-data-md"
                          value={selectedEl.style.fontFamily || FONT_STACKS[0].value}
                          onChange={(e) => applyStyle('font-family', e.target.value)}
                        >
                          {FONT_STACKS.map((f) => (
                            <option key={f.label} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </label>

                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="secondary" onClick={toggleBold}>
                          <Bold className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={toggleItalic}>
                          <Italic className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => applyStyle('text-align', 'left')}>
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => applyStyle('text-align', 'center')}>
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => applyStyle('text-align', 'right')}>
                          <AlignRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}

                  <Button size="sm" variant="danger" onClick={deleteSelected}>
                    <Trash2 className="mr-1 h-4 w-4" /> Delete element
                  </Button>
                </>
              )}
            </CardBody>
          </Card>

          {pendingSuggestions.length > 0 && (
            <Card>
              <CardHeader
                title={`Suggested fields (${pendingSuggestions.length})`}
                action={
                  <Button size="sm" variant="secondary" onClick={() => acceptSuggestion.mutate(pendingSuggestions)}>
                    <Wand2 className="mr-1 h-4 w-4" /> Link all
                  </Button>
                }
              />
              <CardBody className="space-y-2">
                <p className="text-body-sm text-on-surface-variant">
                  Replaces the value printed on the imported PDF with a live field.
                </p>
                {pendingSuggestions.map((s) => (
                  <div key={`${s.id}-${s.token}`} className="rounded border border-outline p-2">
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-data-sm">{`{{${s.token}}}`}</code>
                      <Button size="sm" variant="ghost" onClick={() => acceptSuggestion.mutate([s])}>
                        Link
                      </Button>
                    </div>
                    <div className="truncate text-body-sm text-on-surface-variant" title={s.reason}>
                      {s.matched}
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Insert a field" />
            <CardBody className="max-h-72 space-y-3 overflow-auto">
              {tokenGroups.map((g) => (
                <div key={g.group}>
                  <div className="mb-1 text-body-sm font-medium text-on-surface-variant">{g.group}</div>
                  <div className="flex flex-wrap gap-1">
                    {g.tokens.filter((t) => !t.block && !t.inLoop).map((t) => (
                      <button
                        key={t.token}
                        type="button"
                        title={t.label}
                        onClick={() => insertToken(t.token)}
                        className="rounded border border-outline px-2 py-1 text-data-sm hover:bg-surface-variant"
                      >
                        <Type className="mr-1 inline h-3 w-3" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          {localAssets.length > 0 && (
            <Card>
              <CardHeader title={`Images (${localAssets.length})`} />
              <CardBody className="space-y-2">
                {localAssets.map((a) => (
                  <div key={a.assetId} className="flex items-center gap-2 rounded border border-outline p-2">
                    <img src={a.url} alt="" className="h-8 w-8 object-contain" />
                    <div className="flex-1 text-body-sm capitalize">{a.kind}</div>
                    <Input
                      type="number"
                      className="w-16"
                      value={a.width}
                      onChange={(e) => {
                        const width = Number(e.target.value) || 0;
                        setLocalAssets((prev) =>
                          prev.map((x) => (x.assetId === a.assetId ? { ...x, width } : x)),
                        );
                        updateAsset.mutate({ assetId: a.assetId, patch: { width } });
                      }}
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeAsset.mutate(a.assetId)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      <Modal open={previewHtml !== null} onClose={() => setPreviewHtml(null)} title="Preview with sample data" size="xl">
        {/* An iframe, not dangerouslySetInnerHTML: the preview carries its own
            page-sized stylesheet, and injecting that into the app would restyle
            the settings screen around it. */}
        <iframe
          title="Template preview"
          className="h-[70vh] w-full border-0 bg-white"
          sandbox=""
          srcDoc={previewHtml ?? ''}
        />
      </Modal>
    </div>
  );
}

export default TemplateDesignEditor;
